import React from 'react';
import styled from '@emotion/styled';
import DeviceSettings from './deviceSettings';

import { useInterval, useLicenses, useSelector, useDispatch } from '~Hooks';
import * as actions from '~actions';

import Inventory from './inventory';
import Tables from './tables';
import Reports from './reports';
import Cash from './cash';
import OrderStatus from './orderStatus';

interface Props {
  venueId: string;
}

const ONE_HOUR_MS = 360000;

const LoadingOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
`;

const Synchronizers: React.FunctionComponent<Props> = ({ venueId }) => {
  const companyFlags = useSelector((state) => state.companyFlags);
  const paymentMethods = useSelector((state) => state.POSPaymentMethods);
  const licenses = useLicenses();
  const dispatch = useDispatch();

  const hasCash = paymentMethods.some((p) => p.id === 'CASH');

  // Log event count to track performance impact, done in redux middleware
  useInterval(() => {
    dispatch({ type: actions.LOG_EVENT_COUNT });
  }, ONE_HOUR_MS);

  const anyTypeOfHandheldLicense =
    licenses.hasHandheldOrder || licenses.hasHandheldPay || licenses.hasHandheldPro;

  return (
    <>
      {(licenses.hasQSR || licenses.hasTSR || licenses.hasEPOS || anyTypeOfHandheldLicense) && (
        <Inventory venueId={venueId} />
      )}

      {/* Also include QSR here since Parked Orders uses cheques for saving an order */}
      {(licenses.hasQSR || licenses.hasTSR || anyTypeOfHandheldLicense) && (
        <Tables venueId={venueId} />
      )}

      {(licenses.hasQSR || licenses.hasTSR || licenses.hasEPOS || anyTypeOfHandheldLicense) && (
        <Reports venueId={venueId} />
      )}
      {hasCash && <Cash venueId={venueId} />}

      {/* If there is atleast one device with this license the others need to support sending orders to those displays */}
      {(licenses.hasKDS ||
        licenses.hasCustomerDisplay ||
        licenses.atleastOneDeviceHasKDS ||
        licenses.atleastOneDeviceHasCustomerDisplay) && <OrderStatus venueId={venueId} />}

      {companyFlags.useNewLicenses && <DeviceSettings venueId={venueId} />}
    </>
  );
};

export default Synchronizers;
